<template>
  <ValidationProvider
    :rules="validationRules"
    class="question max-diff column is-10"
    ref="screenValidation"
    tag="div"
    v-slot="{ errors }"
  >
    <div class="question-text">
      <p class="question-text__code" v-if="showQuestionCode">
        {{ question.code }}
      </p>
      <p class="question-text__question" v-html="decodeHtml(question.question[lang])"></p>
      <p class="help is-danger" v-if="errors[0] || conflictMessage">
        {{ errors[0] || conflictMessage }}
      </p>
      <p class="help" v-else>{{ questionHelp }}</p>
    </div>

    <div class="question-content card">
      <!-- Nothing to show, and saying so. Rendering the empty grid instead left the
           respondent staring at two column headings with no rows, blocked by a
           "this question is mandatory" message about a question that had nothing in
           it to answer. -->
      <div class="card-content" v-if="isUnusable">
        <p class="max-diff-unusable">{{ $t("max_diff.unusable") }}</p>
      </div>

      <div class="card-content" v-else>
        <div class="set-progress" v-if="sets.length > 1">
          <span>{{ $t("max_diff.screen_of", { current: currentSetIndex + 1, total: sets.length }) }}</span>
          <span class="set-progress__hint" v-if="!currentScreenAnswered">
            {{ $t("max_diff.pick_both") }}
          </span>
        </div>

        <!-- What the validation rule reads. The question is answered only once every
             screen behind it is, so an unfinished screen has to look empty to the
             mandatory rule, which is what stops the survey's Next button here. -->
        <input :value="completionToken" type="hidden" />

        <table class="table is-hidden-touch">
          <colgroup>
            <col class="col-opt" />
            <col />
            <col class="col-opt" />
          </colgroup>
          <tr class="head-row">
            <td
              :class="{ 'is-missing': columnIsMissing(MOST, errors) }"
              class="has-text-centered label-most"
            >{{ labelMost }}</td>
            <td></td>
            <td
              :class="{ 'is-missing': columnIsMissing(LEAST, errors) }"
              class="has-text-centered label-least"
            >{{ labelLeast }}</td>
          </tr>
          <tr :key="item.code" v-for="item in currentItems">
            <td class="has-text-centered">
              <b-radio
                :disabled="loading"
                :native-value="item.code"
                :value="pickedMost"
                @input="choose('most', item.code)"
              >
              </b-radio>
            </td>
            <td class="item-cell">
              <div class="item-text" v-html="item.question[lang]"></div>
            </td>
            <td class="has-text-centered">
              <b-radio
                :disabled="loading"
                :native-value="item.code"
                :value="pickedLeast"
                @input="choose('least', item.code)"
              >
              </b-radio>
            </td>
          </tr>
        </table>

        <div class="is-mobile is-hidden-desktop">
          <b-collapse
            :key="column.side"
            :open="openColumn === column.side"
            @open="openColumn = column.side"
            animation="slide"
            class="card p-2 mb-2"
            v-for="column in columns"
          >
            <template #trigger="props">
              <div
                :class="{ active: props.open }"
                class="card-header"
                role="button"
              >
                <div class="card-header-control">
                  <p
                    class="card-header-title"
                    :class="[
                      `is-${column.side}`,
                      { 'is-missing': columnIsMissing(column.side, errors) },
                    ]"
                  >
                    {{ column.label }}
                  </p>
                  <a class="card-header-icon">
                    <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                  </a>
                </div>
                <div
                  class="selected-option"
                  v-if="!props.open && pickedText(column.side)"
                >
                  <b-icon icon="check"></b-icon>
                  <span v-html="pickedText(column.side)"></span>
                </div>
              </div>
            </template>

            <div class="card-content">
              <div class="content">
                <b-field
                  :key="item.code"
                  class="mb-4"
                  v-for="item in currentItems"
                >
                  <b-radio
                    :disabled="loading"
                    :native-value="item.code"
                    :value="pickedOn(column.side)"
                    @input="chooseOnMobile(column.side, item.code)"
                  >
                    <span class="item-text" v-html="item.question[lang]"></span>
                  </b-radio>
                </b-field>
              </div>
            </div>
          </b-collapse>
        </div>

      </div>
    </div>
  </ValidationProvider>
</template>

<script>
import { QuestionMixin } from "@/mixins/question";
import { questionKey } from "@/utils/question";
import {
  pickDesignVersion,
  resolveSet,
  buildScreenChanges,
  isScreenAnswered,
  resumeScreenIndex,
  orderWithinScreen,
  nextOpenColumn,
} from "@/utils/max-diff";

const MOST = "most";
const LEAST = "least";

export default {
  name: "MaxDiff",
  mixins: [QuestionMixin],
  data() {
    return {
      currentSetIndex: 0,
      // Set the moment the respondent does anything of their own. Until then the screen
      // position follows the stored answers; afterwards it follows the respondent.
      respondentActed: false,
      conflictMessage: "",
      openColumn: MOST,
      width: "is-10",
    };
  },
  computed: {
    /**
     * Stable per respondent, so leaving and resuming shows the screens the stored
     * answers refer to. Falls back to the response id, then the survey response uuid.
     */
    respondentKey() {
      return (
        this.respondent?.rid ||
        this.respondent?.uuid ||
        this.$route?.query?.rid ||
        ""
      );
    },

    sets() {
      const versions = this.qOpts.design_versions;
      return pickDesignVersion(versions, this.respondentKey) || [];
    },

    /**
     * Resolved against question.sub_questions in its stored order. The indexes in
     * design_versions are positions in that order, never display positions — see
     * resolveSet for why passing a shuffled array is a silent data bug.
     */
    currentItems() {
      const chosen = resolveSet(
        this.question.sub_questions,
        this.sets[this.currentSetIndex]
      );

      // Which items appear is the arrangement's business; where they sit is not, and
      // leaving them in a fixed order would let position bias into the answers.
      return orderWithinScreen(
        chosen,
        `${this.respondentKey}_${this.currentSetIndex}`
      );
    },

    /**
     * True when this question cannot be answered because it has nothing to show.
     *
     * Either no arrangement pool arrived — which is what a survey cloned from a
     * template used to look like — or the pool is there but this screen resolves to no
     * items, which is what happens if the item list is edited down after the pool was
     * built. Both leave a respondent with an empty grid they cannot satisfy and a
     * mandatory rule they cannot pass.
     *
     * It deliberately does not let them through. A blank answer recorded as complete is
     * the failure this question type has already produced twice; a respondent stopped
     * by a message that names the fault is at least a fault someone can report.
     */
    isUnusable() {
      return !this.sets.length || !this.currentItems.length;
    },

    labelMost() {
      return this.answerText(MOST);
    },

    labelLeast() {
      return this.answerText(LEAST);
    },

    pickedMost() {
      return this.pickedOn(MOST);
    },

    pickedLeast() {
      return this.pickedOn(LEAST);
    },

    currentCodes() {
      return this.currentItems.map((item) => item.code);
    },

    currentAnswers() {
      const current = {};
      this.currentCodes.forEach((code) => {
        current[code] = this.value[this.keyFor(code)] || "";
      });
      return current;
    },

    currentScreenAnswered() {
      return isScreenAnswered(this.currentCodes, this.currentAnswers);
    },

    /**
     * What the mandatory rule sees.
     *
     * Empty until both columns of the screen in front of the respondent are picked, so
     * an unfinished screen fails validation exactly the way an unanswered question of
     * any other type does. That is what holds the survey's Next button: onSubmit runs
     * the observer first and never reaches the screen guard while this is blank.
     *
     * It reports the current screen rather than the whole question on purpose — judging
     * the whole thing would fail validation on screen one and the respondent could
     * never move at all.
     */
    completionToken() {
      return this.currentScreenAnswered ? "answered" : "";
    },

    isFirstScreen() {
      return this.currentSetIndex === 0;
    },

    isLastScreen() {
      return this.currentSetIndex >= this.sets.length - 1;
    },

    MOST() {
      return MOST;
    },

    LEAST() {
      return LEAST;
    },

    columns() {
      return [
        { side: MOST, label: this.labelMost },
        { side: LEAST, label: this.labelLeast },
      ];
    },
  },
  methods: {
    answerText(code) {
      const answer = (this.question.answers || []).find((a) => a.code === code);
      if (!answer) return "";
      return answer.answer[this.lang] || Object.values(answer.answer)[0];
    },

    keyFor(itemCode) {
      return questionKey(this.question.qid, itemCode, this.currentSetIndex);
    },

    /**
     * Whether a column should be marked as still owing an answer.
     *
     * Only once the respondent has tried to move on and been stopped. `errors` is empty
     * until the survey runs validation, and clearScreenValidation() resets it on every
     * screen change, so a screen they have not yet attempted never shows red — which is
     * what marking on sight would do, painting both columns before anyone had done
     * anything wrong.
     *
     * @param {string} side  MOST or LEAST
     * @param {Array} errors the validation slot's errors for this question
     */
    columnIsMissing(side, errors) {
      return (errors || []).length > 0 && !this.pickedOn(side);
    },

    pickedOn(side) {
      const item = this.currentItems.find(
        (i) => this.value[this.keyFor(i.code)] === side
      );
      return item ? item.code : null;
    },

    /**
     * Emits every item on the screen, not only the two that were picked.
     *
     * The unpicked rows are what make the score's denominator work: they record that
     * an item was shown and passed over. Without them there is no way to tell "never
     * shown" from "shown and not chosen".
     */
    choose(side, itemCode) {
      const codes = this.currentItems.map((item) => item.code);

      const current = {};
      codes.forEach((code) => {
        current[code] = this.value[this.keyFor(code)] || "";
      });

      const { values, clashed } = buildScreenChanges(codes, current, side, itemCode);

      const changes = {};
      codes.forEach((code) => {
        changes[this.keyFor(code)] = values[code];
      });

      this.conflictMessage = clashed ? this.$t("max_diff.cannot_be_both") : "";
      this.respondentActed = true;

      this.$emit("input", changes);
    },

    /**
     * Text of the item picked in a column, for the collapsed header summary.
     */
    pickedText(side) {
      const code = this.pickedOn(side);
      if (!code) return "";

      const item = this.currentItems.find((i) => i.code === code);
      if (!item) return "";

      return item.question[this.lang] || Object.values(item.question)[0];
    },

    /**
     * On mobile the two columns are separate cards, so picking in one should move the
     * respondent to the other rather than leaving them to find it. Mirrors how the
     * matrix component advances between its rows.
     */
    chooseOnMobile(side, itemCode) {
      this.choose(side, itemCode);

      this.$nextTick(() => {
        const open = nextOpenColumn(this.currentCodes, this.currentAnswers, side);
        if (open) this.openColumn = open;
      });
    },

    /**
     * Move to the next screen. Called by the view when the respondent presses the
     * survey's own Next button.
     *
     * Whether the current screen is answered is not checked here — validation already
     * settled that before the view got this far. Returning false means "there was no
     * screen to move to", which is the view's signal to submit the page instead.
     *
     * @returns {boolean} true if a screen was consumed
     */
    goToNextScreen() {
      if (this.isLastScreen) return false;

      this.conflictMessage = "";
      this.openColumn = MOST;
      this.respondentActed = true;
      this.currentSetIndex++;
      this.emitScreen();
      this.clearScreenValidation();

      return true;
    },

    /**
     * Move to the previous screen, for the survey's own Back button.
     *
     * Nothing is restored by hand: the selections are read straight out of `value`,
     * which the survey keeps, so going back shows what was picked before.
     *
     * @returns {boolean} true if a screen was consumed
     */
    goToPreviousScreen() {
      if (this.isFirstScreen) return false;

      this.conflictMessage = "";
      this.openColumn = MOST;
      this.respondentActed = true;
      this.currentSetIndex--;
      this.emitScreen();
      this.clearScreenValidation();

      return true;
    },

    /**
     * Drop the verdict the previous screen earned.
     *
     * The provider is one field standing for whichever screen is showing, so without
     * this a respondent who answers one screen and moves to the next is met by a
     * mandatory error for a screen they have not had the chance to answer yet.
     */
    clearScreenValidation() {
      this.$nextTick(() => {
        if (this.$refs.screenValidation) this.$refs.screenValidation.reset();
      });
    },

    /**
     * Tell the view where the respondent is.
     *
     * The view cannot read this off a ref, because refs are not reactive and the label
     * on the Next button has to change the moment the last screen is reached.
     */
    emitScreen() {
      this.$emit("screen-change", {
        qid: this.question.qid,
        index: this.currentSetIndex,
        total: this.sets.length,
      });
    },
  },

  watch: {
    /**
     * Answers do not arrive with the component.
     *
     * Coming back to a page, the survey re-renders the question and only then assigns
     * the answers it fetched, so at created() the stored picks are not there yet and
     * the question would open on screen one — the exact thing the resume exists to
     * prevent. Following `value` instead of a single reading of it is what makes the
     * resume land where the respondent actually stopped.
     *
     * It gives way the moment the respondent acts, or it would drag them back out of
     * any screen they chose to revisit.
     */
    value: {
      immediate: true,
      handler() {
        if (this.respondentActed) return;

        this.currentSetIndex = resumeScreenIndex(
          this.sets,
          this.question.sub_questions,
          this.value,
          this.question.qid
        );

        this.emitScreen();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.max-diff {
  color: #595959;

  .question-content.card {
    overflow-x: auto;
  }

  .set-progress {
    /* A column rather than a wrapping row. The two carry different things — where the
       respondent is, and what is being asked of them — and side by side they run into
       one sentence, which in Japanese has no space between them to read as a seam.
       A narrow screen already broke them apart; this makes every screen agree. */
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    font-size: 0.875rem;
    color: #929292;
    margin-bottom: 0.75rem;

    &__hint {
      color: #b0b0b0;
    }
  }

  .table {
    width: 100%;

    /* Fixed rather than a percentage: at a wide viewport a percentage column left the
       radio floating in a few hundred pixels of dead space, far from the text it
       belongs to, while the wrapping item text was squeezed into the middle. */
    .col-opt {
      width: 132px;
    }

    td {
      vertical-align: middle;
    }

    .item-cell {
      /* Centred so a short item sits midway between the two columns, which is where
         the sandwich layout leads the eye. */
      text-align: center;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    .head-row td {
      color: #929292;
      font-size: 0.875rem;
      font-weight: 700;
      padding-bottom: 0.6rem;
    }

    .label-most {
      color: #00bd9d;
    }

    .label-least {
      color: #097860;
    }

    /* Marks the column the respondent still owes an answer in, once they have tried to
       move on and been stopped. Colour alone would leave anyone who cannot separate red
       from green with nothing to go on, so the underline carries the same meaning. */
    .head-row td.is-missing {
      color: #f14668;
      text-decoration: underline;
      text-underline-offset: 0.2rem;
    }

    /* Left-aligned inside a centred block: a long item centred line by line is ragged
       on both edges and harder to read, while a short item's block shrinks to its
       content so it still reads as centred.

       The cap is in em, not ch. ch measures the width of "0", but Japanese glyphs are
       about twice that, so a ch-based cap wrapped Japanese items at roughly half the
       characters an English item got — and wrapped them while the column still had
       room. At 46em the column width is what actually constrains normal viewports;
       the cap only bites on very wide screens, where it stops a line running the full
       width of the monitor. */
    .item-text {
      line-height: 1.45;
      display: inline-block;
      max-width: 46em;
      text-align: left;

      /* Item text is pasted in by the researcher, and a run with nowhere to break — a
         URL, a file path, code copied from a page whose spaces arrived as &nbsp; — sets
         the column's minimum width. That widens the whole table past the card, which
         pushes the "least" column off the side of the screen.

         anywhere rather than break-word: both split the run mid-token, but only anywhere
         also lowers the minimum width the column reports, which is what keeps the table
         inside the card to begin with. word-break repeats it for Safari before 15.4,
         where overflow-wrap: anywhere is ignored. */
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  .is-mobile {
    .card-header {
      box-shadow: none;
      display: block;
      padding: 0.25rem 0;
    }

    .card-header-control {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .card-header-title {
      font-weight: 600;
      padding: 0.4rem 0;

      &.is-most {
        color: #00bd9d;
      }

      &.is-least {
        color: #097860;
      }

      &.is-missing {
        color: #f14668;
        text-decoration: underline;
        text-underline-offset: 0.2rem;
      }
    }

    /* The same pasted text, in a column narrower still. */
    .item-text,
    .selected-option span {
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .selected-option {
      display: flex;
      align-items: flex-start;
      gap: 0.35rem;
      color: #00bd9d;
      font-size: 0.82rem;
      line-height: 1.35;
      margin-bottom: 0.6rem;
    }

    .card-content {
      padding: 0.6rem 0 0;
    }
  }
}
</style>
